import type { FC } from 'react';
import { WrapItem } from '@chakra-ui/react';
import { useCurrentUser } from '../../hooks';
import { QText } from '../../QAtoms';
import { DataProps } from '../../utils';
import { useLibraryTranslation } from '../../i18n';

export type QMetadataBaseItemProps<T> = {
  label: string;
  value: T | null | undefined;
} & DataProps;

/**
 * An arbitrary label and string value pair.
 */
export const QStringMetadataItem: FC<QMetadataBaseItemProps<string>> = ({
  label,
  value,
  ...rest
}) => {
  if (!value) {
    return null;
  }
  return (
    <WrapItem {...rest}>
      <QText fontSize="sm" fontWeight={400}>
        {label}:&nbsp;
      </QText>
      <QText fontSize="sm" fontWeight={600}>
        {value}
      </QText>
    </WrapItem>
  );
};
QStringMetadataItem.displayName = 'QStringMetadataItem';

type QDateMetadataItemProps = QMetadataBaseItemProps<Date> & {
  withTime?: boolean;
};

/**
 * And arbitrary label and date value pair.
 *
 * Relies on the `useCurrentUser` hook to localise the date.
 *
 * @param withTime - Whether to include the time in the formatted date.
 */
export const QDateMetadataItem: FC<QDateMetadataItemProps> = ({
  label,
  value,
  withTime,
  ...rest
}) => {
  const { formatDate } = useCurrentUser();
  if (!value) {
    return null;
  }
  return (
    <QStringMetadataItem
      label={label}
      value={formatDate(value, withTime)}
      {...rest}
    />
  );
};
QDateMetadataItem.displayName = 'QDateMetadataItem';

export type QMetadataProps<T = string> = Pick<
  QMetadataBaseItemProps<T>,
  'value'
>;

export const QVersionMetadata: FC<QMetadataProps> = ({ value, ...rest }) => {
  const { t } = useLibraryTranslation();
  return (
    <QStringMetadataItem
      label={t('metadata.version')}
      value={value}
      {...rest}
    />
  );
};
QVersionMetadata.displayName = 'QVersionMetadata';

export const QOwnerMetadata: FC<QMetadataProps> = ({ value, ...rest }) => {
  const { t } = useLibraryTranslation();
  return (
    <QStringMetadataItem label={t('metadata.owner')} value={value} {...rest} />
  );
};
QOwnerMetadata.displayName = 'QOwnerMetadata';

export const QCreatedAtMetadata: FC<QMetadataProps<Date>> = ({
  value,
  ...rest
}) => {
  const { t } = useLibraryTranslation();
  return (
    <QDateMetadataItem
      label={t('metadata.created')}
      value={value}
      withTime
      {...rest}
    />
  );
};
QCreatedAtMetadata.displayName = 'QCreatedAtMetadata';

export const QLastModifiedMetadata: FC<QMetadataProps<Date>> = ({
  value,
  ...rest
}) => {
  const { t } = useLibraryTranslation();
  return (
    <QDateMetadataItem
      label={t('metadata.lastModified')}
      value={value}
      withTime
      {...rest}
    />
  );
};
QLastModifiedMetadata.displayName = 'QLastModifiedMetadata';

export const QDueMetadata: FC<QMetadataProps<Date>> = ({ value, ...rest }) => {
  const { t } = useLibraryTranslation();
  return (
    <QDateMetadataItem label={t('metadata.due')} value={value} {...rest} />
  );
};
QDueMetadata.displayName = 'QDueMetadata';
